import { ShieldCheck } from "lucide-react";
import { LogoMark } from "@/components/brand/logo";
import { AttackSurface } from "@/components/marketing/attack-surface";
import { branding } from "@/config/branding";

/**
 * WHAT THIS PANEL MAY CLAIM.
 *
 * This is the first page a prospect sees and it is the only marketing surface in
 * the product, so everything on it is a claim the platform has to be able to
 * stand behind. The copy shipped in the vendor drop and made three that the
 * platform cannot:
 *
 *   "AI agents prove every finding with a working PoC"
 *      — false three times over. The AI engine (CAP-011) cannot be selected in
 *        any scan; 1,301 of 1,328 stored findings are unverified; and the
 *        machine reproduction is DELIBERATELY not persisted (pipeline.go,
 *        invariant 4), so there is no PoC to hand over.
 *   "Continuous DAST + live attack-surface mapping"
 *      — scheduling (FR-23) is unbuilt, so nothing is continuous; and CAP-012's
 *        crawled-to-tested loop (FR-76a) is deferred to v2 by the CCB.
 *   "One-click ISO, OWASP, PCI & HIPAA reports"
 *      — FR-62's framework table does not exist (`S-193`). Those report types
 *        are refused at the API BY NAME; only technical detail is producible.
 *
 * Replaced with properties the platform actually holds, each one an invariant
 * this build is gated on rather than a capability that might regress: a claim
 * here should be falsifiable by reading the code, and these are.
 */
const HIGHLIGHTS = [
  "Every finding passes a verification stage no setting can switch off",
  "A finding our own checks contradict is discarded — never quietly downgraded",
  "Each scan runs isolated, and target credentials never leave the vault",
];

/**
 * NOT CERTIFICATIONS. The vendor drop listed "SOC 2", "ISO 27001", "OWASP ASVS"
 * and "PCI-DSS" here, in the visual idiom of a trust-badge row — which asserts
 * third-party attestation about HIBIR THE COMPANY, not about this product.
 * Nothing in this repository evidences any of them, and a badge row is exactly
 * where a reader expects an audited claim.
 *
 * These four are PRODUCT properties, checkable in this codebase, and none of
 * them claims an external auditor. Real certifications belong here when they
 * exist and someone can name the auditor and the report date.
 */
const ASSURANCES = ["Per-scan isolation", "Mandatory verification", "Immutable findings", "Credentials in vault"];

export default function AuthLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid min-h-screen lg:grid-cols-[1.05fr_1fr]">
      {/* Brand panel — dark, cinematic */}
      <div
        className="relative hidden flex-col justify-between overflow-hidden p-12 text-white lg:flex"
        style={{
          background:
            "radial-gradient(1000px 520px at 15% -10%, rgba(109,123,255,0.22), transparent 60%), radial-gradient(820px 480px at 95% 8%, rgba(167,139,250,0.14), transparent 60%), #0a0b12",
        }}
      >
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(255,255,255,0.045) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.045) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
            maskImage: "radial-gradient(120% 95% at 30% 0%, #000 30%, transparent 82%)",
            WebkitMaskImage: "radial-gradient(120% 95% at 30% 0%, #000 30%, transparent 82%)",
          }}
        />
        <AttackSurface className="pointer-events-none absolute -right-[22%] top-1/2 h-[680px] w-[680px] -translate-y-1/2 opacity-70" />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#0a0b12] via-[#0a0b12]/70 to-transparent" />

        {/* top: brand */}
        <div className="relative flex items-center gap-2.5">
          <LogoMark />
          <span className="flex flex-col leading-none">
            <span className="text-[15px] font-bold tracking-tight">{branding.name}</span>
            <span className="mt-0.5 font-mono text-[9px] uppercase tracking-[0.18em] text-indigo-300/70">Offensive Security</span>
          </span>
        </div>

        {/* middle: pitch */}
        <div className="relative max-w-md">
          <h1 className="font-display text-[clamp(2rem,3vw,2.9rem)] font-semibold leading-[1.05] tracking-[-0.02em]">
            {/* 3.104: the landing's own headline (2.995), so the two front doors agree. */}
            Vulnerabilities{" "}
            <span className="bg-gradient-to-r from-indigo-200 via-indigo-400 to-violet-400 bg-clip-text text-transparent">you can act on.</span>
          </h1>
          <p className="mt-5 text-[15px] leading-relaxed text-zinc-400">
            {/* "exploit" and "route fixes into your stack" were both false: the
                platform re-tests to verify (ADR-023) rather than exploiting, and
                webhooks and notifications are unbuilt, so nothing is routed
                anywhere. What it does do is say which findings it could prove. */}
            {/* 2.961 (invariant 14, SARD P4): users choose Scan Profiles, never engines, so the
                roster and its size are not the console's to state, and a count goes stale
                the moment a profile changes. Found in the demo walk-through, 2026-10-06. */}
            Sign in to run scans, see which findings were confirmed, and track them to resolution.
          </p>
          <ul className="mt-8 space-y-3">
            {HIGHLIGHTS.map((h) => (
              <li key={h} className="flex items-center gap-3 text-[13.5px] text-zinc-300">
                <span className="flex size-6 items-center justify-center rounded-md bg-indigo-500/15 text-indigo-300 ring-1 ring-inset ring-indigo-400/20">
                  <ShieldCheck className="size-3.5" />
                </span>
                {h}
              </li>
            ))}
          </ul>
        </div>

        {/* bottom: product assurances — deliberately NOT a certification row */}
        <div className="relative flex flex-wrap items-center gap-x-5 gap-y-2 font-mono text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">
          {ASSURANCES.map((c) => (
            <span key={c} className="flex items-center gap-1.5"><span className="size-1 rounded-full bg-indigo-400/60" /> {c}</span>
          ))}
        </div>
      </div>

      {/* Form side — the page's main landmark (NFR-32, WCAG §1.3.1 / §2.4.1). */}
      <main className="relative flex items-center justify-center bg-background p-6 sm:p-10">
        <div className="w-full max-w-md">
          {children}
          <p className="mt-8 text-center text-xs text-muted-foreground">
            © {branding.year} {branding.company}. All rights reserved.
          </p>
        </div>
      </main>
    </div>
  );
}
